<template>
  <div>
    <h1>Empresas</h1>
    <b-row>
      <b-col>
        <router-link :to="'./create/'">
          <b-button size="sm">{{ $t("geral.create") }}</b-button>
        </router-link>
        <b-button
          variant="danger"
          size="sm"
          class="ml-2"
          :disabled="!selectedRows.length"
          @click="deleteSelected"
        >
          🗑️
        </b-button>
      </b-col>
    </b-row>

    <b-row style="min-height: 500px; flex-grow: 1;">
      <b-col :class="themeClass">
        <ag-grid-vue
          :gridOptions="gridOptions"
          :localeText="localeText"
          :columnDefs="columnDefs"
          :rowData="rowData"
          :defaultColDef="defaultColDef"
          :domLayout="'autoHeight'"
          :pagination="true"
          rowHeight="40"
          :paginationPageSize="15"
          @rowClicked="onRowClicked"
          @grid-ready="onGridReady"
          @first-data-rendered="onFirstDataRendered"
          @selection-changed="onSelectionChanged"
        ></ag-grid-vue>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";

export default {
  components: {
    AgGridVue
  },
  computed: {
    themeClass() {
      return this.$store.state.darkmode
        ? "ag-theme-alpine-dark"
        : "ag-theme-alpine";
    }
  },
  data() {
    return {
      localeText: this.$t('AG_GRID_LOCALE'),
      gridOptions: {
        onGridReady: this.onGridReady,
        rowSelection: "multiple",
        paginationPageSize: 15
      },
      columnDefs: [
        {
          headerName: "Empresa",
          field: "companyName",
          sortable: false,
          filter: true,
          checkboxSelection: true,
          headerCheckboxSelection: true
        },
        {
          headerName: "Nome do Responsável",
          field: "responsibleName",
          sortable: false,
          filter: true
        },
        {
          headerName: "Tipo",
          field: "entityType",
          sortable: false,
          filter: true,
          cellRenderer: params => {
            const types = ["Jurídica", "Física", "Estrangeira"];
            return types[params.value] || "Desconhecido";
          }
        },
        {
          headerName: "Fornecedor",
          field: "isSupplier",
          sortable: false,
          filter: true,
          cellRenderer: params => {
            return params.value
              ? '<span style="color: green;">✅</span>'
              : '<span style="color: red;">❌</span>';
          }
        },
        {
          headerName: "Cliente",
          field: "isPurchaser",
          sortable: false,
          filter: true,
          cellRenderer: params => {
            return params.value
              ? '<span style="color: green;">✅</span>'
              : '<span style="color: red;">❌</span>';
          }
        },
        {
          headerName: "NFe",
          field: "invoiceNFe",
          sortable: false,
          filter: true,
          cellRenderer: params => {
            return params.value
              ? '<span style="color: green;">✅</span>'
              : '<span style="color: red;">❌</span>';
          }
        },
        {
          headerName: "NFsE",
          field: "invoiceNFsE",
          sortable: false,
          filter: true,
          cellRenderer: params => {
            return params.value
              ? '<span style="color: green;">✅</span>'
              : '<span style="color: red;">❌</span>';
          }
        },
        {
          headerName: "NFcE",
          field: "invoiceNFcE",
          sortable: false,
          filter: true,
          cellRenderer: params => {
            return params.value
              ? '<span style="color: green;">✅</span>'
              : '<span style="color: red;">❌</span>';
          }
        },
        {
          headerName: "Criado por",
          field: "createdByUserName",
          sortable: false,
          filter: true
        },
        {
          headerName: "Criado em",
          field: "createdAt",
          sortable: false,
          filter: true,
          valueFormatter: ({ value }) => {
            if (!value) return "";

            const date = new Date(value);

            const formatTwoDigits = num => num.toString().padStart(2, "0");

            const formattedDate = [
              formatTwoDigits(date.getDate()),
              formatTwoDigits(date.getMonth() + 1),
              date.getFullYear()
            ].join("/");

            const formattedTime = [
              formatTwoDigits(date.getHours()),
              formatTwoDigits(date.getMinutes())
            ].join(":");

            return `${formattedDate} ${formattedTime}`;
          }
        },
        {
          headerName: "Editado por",
          field: "updatedByUserName",
          sortable: false,
          filter: true
        },
        {
          headerName: "Editado em",
          field: "updatedAt",
          sortable: false,
          filter: true,
          valueFormatter: ({ value }) => {
            if (!value) return "";

            const date = new Date(value);

            const formatTwoDigits = num => num.toString().padStart(2, "0");

            const formattedDate = [
              formatTwoDigits(date.getDate()),
              formatTwoDigits(date.getMonth() + 1),
              date.getFullYear()
            ].join("/");

            const formattedTime = [
              formatTwoDigits(date.getHours()),
              formatTwoDigits(date.getMinutes())
            ].join(":");

            return `${formattedDate} ${formattedTime}`;
          }
        }
      ],
      rowData: [],
      defaultColDef: {
        resizable: true,
        flex: 1
      },
      selectedRows: []
    };
  },
  methods: {
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.$apiScheduler
        .get("/entity/list")
        .then(response => {
          this.rowData = response.data;
        })
        .catch(error => {
          if (error.response.status == 401) {
            this.$store.dispatch("logout").then(() => {
              this.$router.push("/login");
            });
          } else {
            callback([]);
          }
        });
    },
    onRowClicked(event) {
      const rowData = event.data;
      this.$router.push({
        name: "EnterpriseCrud",
        params: { rowData: JSON.stringify(rowData), edit: true }
      });
    },
    onFirstDataRendered() {
      const allColumnIds = [];
      this.gridColumnApi.getAllColumns().forEach(col => {
        allColumnIds.push(col.getColId());
      });
      this.gridColumnApi.autoSizeColumns(allColumnIds);
    },
    onSelectionChanged() {
      if (this.gridOptions.api) {
        this.selectedRows = this.gridOptions.api
          .getSelectedNodes()
          .map(node => node.data);
      }
    },
    deleteSelected() {
      const selectedIds = this.selectedRows.map(row => row.id);
      if (selectedIds.length === 0) return;

      if (!confirm("Tem certeza que deseja remover as empresas selecionadas?"))
        return;

      this.$apiScheduler
        .post("/entity/delete", selectedIds)
        .then(() => {
          this.rowData = this.rowData.filter(
            row => !selectedIds.includes(row.id)
          );
          this.gridOptions.api.setRowData(this.rowData);
          alert("Empresas removidas com sucesso!");
        })
        .catch(err => {
          console.error(err);
          alert("Erro ao remover empresas.");
        });
    }
  }
};
</script>

<style>
/* MODO ESCURO */
.ag-root,
.ag-root-wrapper,
.ag-row,
.ag-cell,
.ag-header-cell,
.ag-header-cell-label {
  background-color: #1e1e2f !important;
  color: #ffffffb3 !important;
  font-family: "Poppins", sans-serif;
  font-size: 12px;
  border: none !important;
}

/* MODO CLARO */
.ag-theme-alpine .ag-root,
.ag-theme-alpine .ag-root-wrapper,
.ag-theme-alpine .ag-row,
.ag-theme-alpine .ag-cell,
.ag-theme-alpine .ag-header-cell,
.ag-theme-alpine .ag-header-cell-label {
  background-color: #f5f6fa !important;
  color: #191919 !important;
  font-family: "Poppins", sans-serif;
  font-size: 12px;
  border: none !important;
}
</style>
